// src/hooks/useWildfireAlerts.js
import { useEffect, useState } from "react";
// import { useCounty } from "../components/CountyContext";
import { useCounty } from "../context/CountyContext";
import { mapWildfireIncidentToAlert } from "../utils/mapWildfireIncidentToAlert";

// Texas-only counties from TxDOT (public)
const COUNTY_LAYER_URL =
  "https://services.arcgis.com/KTcxiTD9dsQw4r7Z/arcgis/rest/services/Texas_County_Boundaries_Detailed/FeatureServer/0";

// County name field in this layer
const COUNTY_NAME_FIELD = "CNTY_NM";

// USA Current Wildfires incidents layer
const WILDFIRE_INCIDENTS_URL =
  "https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/USA_Wildfires_v1/FeatureServer/0";

export function useWildfireAlerts(options) {
  const bufferMiles = (options && options.bufferMiles) || 25;
  const { selectedCounty } = useCounty();

  const [wildfireAlerts, setWildfireAlerts] = useState([]);
  const [isLoadingWildfire, setIsLoadingWildfire] = useState(false);
  const [wildfireError, setWildfireError] = useState(null);

  useEffect(
    function () {
      if (!selectedCounty) {
        setWildfireAlerts([]);
        return;
      }

      // Avoid running in SSR
      if (typeof window === "undefined") {
        return;
      }

      let cancelled = false;

      async function load() {
        setIsLoadingWildfire(true);
        setWildfireError(null);

        try {
          const [
            FeatureLayerModule,
            geometryEngineModule,
          ] = await Promise.all([
            import("@arcgis/core/layers/FeatureLayer"),
            import("@arcgis/core/geometry/geometryEngine"),
          ]);

          const FeatureLayer = FeatureLayerModule.default;
          const geometryEngine = geometryEngineModule;

          const countyLayer = new FeatureLayer({
            url: COUNTY_LAYER_URL,
          });

          const firesLayer = new FeatureLayer({
            url: WILDFIRE_INCIDENTS_URL,
          });

          // 1) Query county polygon by name (TxDOT uses CNTY_NM)
          const countyQuery = countyLayer.createQuery();
          const safeName = selectedCounty.replace(/'/g, "''");

          countyQuery.where =
            COUNTY_NAME_FIELD + " = '" + safeName + "'";
          countyQuery.returnGeometry = true;
          countyQuery.outFields = [COUNTY_NAME_FIELD, "CNTY_FIPS"];

          // console.log(
          //   "[useWildfireAlerts] querying TxDOT county",
          //   selectedCounty,
          //   "with where:",
          //   countyQuery.where
          // );

          const countyResult = await countyLayer.queryFeatures(countyQuery);
          // console.log(
          //   "[useWildfireAlerts] TxDOT county matches:",
          //   countyResult.features &&
          //     countyResult.features.map(function (f) {
          //       return f.attributes;
          //     })
          // );

          if (
            !countyResult.features ||
            countyResult.features.length === 0
          ) {
            if (!cancelled) {
              // console.warn(
              //   "[useWildfireAlerts] No TxDOT county found for",
              //   selectedCounty
              // );
              setWildfireAlerts([]);
            }
            return;
          }

          const countyGeom = countyResult.features[0].geometry;

          // 2) Buffer county
          const buffered = geometryEngine.geodesicBuffer(
            countyGeom,
            bufferMiles,
            "miles"
          );

          // 3) Query wildfires intersecting buffer
          const fireQuery = firesLayer.createQuery();
          fireQuery.geometry = buffered;
          fireQuery.spatialRelationship = "intersects";
          fireQuery.returnGeometry = false;

          // SAFE: request all fields to avoid invalid outFields
          fireQuery.outFields = ["*"];

          const fireResult = await firesLayer.queryFeatures(fireQuery);
          const features = fireResult.features || [];
          // console.log(
          //   "[useWildfireAlerts] incidents found for",
          //   selectedCounty,
          //   "within",
          //   bufferMiles,
          //   "miles:",
          //   features.length
          // );
          // if (features[0]) {
          //   console.log(
          //     "[useWildfireAlerts] first incident attrs",
          //     features[0].attributes
          //   );
          // }

          const alerts = features.map(function (f) {
            return mapWildfireIncidentToAlert(f);
          });

          if (!cancelled) {
            setWildfireAlerts(alerts);
          }
        } catch (err) {
          if (!cancelled) {
            console.error("[useWildfireAlerts] error", err);
            setWildfireError(err);
            setWildfireAlerts([]);
          }
        } finally {
          if (!cancelled) {
            setIsLoadingWildfire(false);
          }
        }
      }

      load();

      return function cleanup() {
        cancelled = true;
      };
    },
    [selectedCounty, bufferMiles]
  );

  return { wildfireAlerts, isLoadingWildfire, wildfireError };
}
